/**
 * Auth Hooks - React hooks for authentication
 * Provides easy-to-use hooks for auth state and actions
 * Based on Wasilah Platform Access Control
 */

import { useCallback, useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'
import {
  useAuthStore,
  selectIsAuthenticated,
  selectUser,
  selectUserProfile,
  selectIsLoading,
  selectError,
  selectUserRoles,
} from './auth-store'
import {
  type Permission,
  CLIENT_ROLES,
  PROVIDER_ROLES,
  BACKOFFICE_ROLES,
  PERMISSIONS,
} from './permissions'

/**
 * Main auth hook - provides all auth state and actions
 */
export const useAuth = () => {
  const { user, isAuthenticated, isLoading, error, userProfile, login, logout, clearError } =
    useAuthStore(
      useShallow(state => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        isLoading: state.isLoading,
        error: state.error,
        userProfile: state.userProfile,
        login: state.login,
        logout: state.logout,
        clearError: state.clearError,
      }))
    )

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    userProfile,
    login,
    logout,
    clearError,
  }
}

/**
 * Hook for checking if user is authenticated
 */
export const useIsAuthenticated = (): boolean => {
  return useAuthStore(selectIsAuthenticated)
}

/**
 * Hook for getting current user
 */
export const useUser = () => {
  return useAuthStore(selectUser)
}

/**
 * Hook for getting user profile data
 */
export const useUserProfile = () => {
  return useAuthStore(selectUserProfile)
}

/**
 * Hook for auth loading state
 */
export const useAuthLoading = (): boolean => {
  return useAuthStore(selectIsLoading)
}

/**
 * Hook for auth errors
 */
export const useAuthError = () => {
  return useAuthStore(selectError)
}

/**
 * Hook for user roles
 */
export const useUserRoles = (): string[] => {
  return useAuthStore(selectUserRoles)
}

/**
 * Hook for role-based access control
 */
export const useHasRole = (role: string): boolean => {
  const roles = useUserRoles()
  return roles.includes(role)
}

/**
 * Hook for checking multiple roles (any match)
 */
export const useHasAnyRole = (requiredRoles: string[]): boolean => {
  const roles = useUserRoles()
  return useMemo(() => requiredRoles.some(role => roles.includes(role)), [roles, requiredRoles])
}

/**
 * Hook for checking all roles required
 */
export const useHasAllRoles = (requiredRoles: string[]): boolean => {
  const roles = useUserRoles()
  return useMemo(() => requiredRoles.every(role => roles.includes(role)), [roles, requiredRoles])
}

/**
 * Hook for protected route logic
 * Fixed: Wait for roles to be loaded before checking authorization
 */
export const useProtectedRoute = (requiredRoles?: string[]) => {
  const isAuthenticated = useIsAuthenticated()
  const isLoading = useAuthLoading()
  const userRoles = useUserRoles()
  const hasRequiredRoles = useHasAnyRole(requiredRoles ?? [])
  const { login } = useAuthStore(
    useShallow(state => ({
      login: state.login,
    }))
  )

  // Check if roles are still being loaded
  // If authenticated but no roles yet, we're still initializing
  const rolesStillLoading = isAuthenticated && userRoles.length === 0 && !isLoading

  // Only check authorization after roles are loaded
  const isAuthorized =
    isAuthenticated && (!requiredRoles || requiredRoles.length === 0 || hasRequiredRoles)

  // Still loading if auth is loading OR if authenticated but roles not yet available
  const stillLoading = isLoading || rolesStillLoading

  const redirectToLogin = useCallback(
    (returnPath?: string) => {
      login(returnPath ?? (typeof window !== 'undefined' ? window.location.pathname : '/'))
    },
    [login]
  )

  return {
    isAuthenticated,
    isAuthorized,
    isLoading: stillLoading,
    redirectToLogin,
  }
}

/**
 * Hook for login with redirect
 */
export const useLogin = () => {
  const { login } = useAuthStore(
    useShallow(state => ({
      login: state.login,
    }))
  )

  return useCallback(
    (redirectPath?: string) => {
      login(redirectPath)
    },
    [login]
  )
}

/**
 * Hook for logout
 */
export const useLogout = () => {
  const { logout } = useAuthStore(
    useShallow(state => ({
      logout: state.logout,
    }))
  )

  return logout
}

/**
 * Hook for getting access token (useful for API calls)
 */
export const useAccessToken = (): string | null => {
  const user = useUser()
  return user?.access_token ?? null
}

// =============================================================================
// Wasilah Platform Permission Hooks
// =============================================================================

/**
 * Hook for checking a specific permission
 */
export const useHasPermission = (permission: Permission): boolean => {
  const roles = useUserRoles()
  return roles.includes(permission)
}

/**
 * Hook for checking multiple permissions (any match)
 */
export const useHasAnyPermission = (permissions: Permission[]): boolean => {
  const roles = useUserRoles()
  return useMemo(() => permissions.some(p => roles.includes(p)), [roles, permissions])
}

/**
 * Hook for checking if user is a client (Client Portal - hospitals/clinics)
 */
export const useIsClient = (): boolean => {
  const roles = useUserRoles()
  return useMemo(() => Object.values(CLIENT_ROLES).some(role => roles.includes(role)), [roles])
}

/**
 * Hook for checking if user is a provider (Provider Portal - suppliers)
 */
export const useIsProvider = (): boolean => {
  const roles = useUserRoles()
  return useMemo(() => Object.values(PROVIDER_ROLES).some(role => roles.includes(role)), [roles])
}

/**
 * Hook for checking if user is backoffice
 */
export const useIsBackoffice = (): boolean => {
  const roles = useUserRoles()
  return useMemo(() => Object.values(BACKOFFICE_ROLES).some(role => roles.includes(role)), [roles])
}

/**
 * Hook for checking if user is any admin
 */
export const useIsAdmin = (): boolean => {
  const roles = useUserRoles()
  return useMemo(
    () =>
      roles.includes(CLIENT_ROLES.ADMIN) ||
      roles.includes(PROVIDER_ROLES.ADMIN) ||
      roles.includes(BACKOFFICE_ROLES.ADMIN),
    [roles]
  )
}

/**
 * Hook for checking if user is platform admin
 */
export const useIsPlatformAdmin = (): boolean => {
  const roles = useUserRoles()
  return roles.includes(BACKOFFICE_ROLES.ADMIN)
}

/**
 * Hook for getting user type
 */
export const useUserType = (): 'clients' | 'providers' | 'backoffice' | null => {
  const isClient = useIsClient()
  const isProvider = useIsProvider()
  const isBackoffice = useIsBackoffice()

  if (isBackoffice) return 'backoffice'
  if (isClient) return 'clients'
  if (isProvider) return 'providers'
  return null
}

// =============================================================================
// Convenience Permission Hooks
// =============================================================================

/** Hook for checking if user can view requests */
export const useCanViewRequests = (): boolean => useHasPermission(PERMISSIONS.REQUESTS_VIEW)

/** Hook for checking if user can create requests */
export const useCanCreateRequests = (): boolean => useHasPermission(PERMISSIONS.REQUESTS_CREATE)

/** Hook for checking if user can manage orders */
export const useCanManageOrders = (): boolean => useHasPermission(PERMISSIONS.ORDERS_MANAGE)

/** Hook for checking if user can view RFQs */
export const useCanViewRfqs = (): boolean => useHasPermission(PERMISSIONS.RFQ_VIEW)

/** Hook for checking if user can respond to RFQs */
export const useCanRespondToRfqs = (): boolean => useHasPermission(PERMISSIONS.RFQ_RESPOND)

/** Hook for checking if user can manage quotes */
export const useCanManageQuotes = (): boolean => useHasPermission(PERMISSIONS.QUOTES_MANAGE)

/** Hook for checking if user can manage organization users */
export const useCanManageOrgUsers = (): boolean => useHasPermission(PERMISSIONS.ORG_USERS_MANAGE)

/** Hook for checking if user can manage organization settings */
export const useCanManageOrgSettings = (): boolean => useHasPermission(PERMISSIONS.ORG_SETTINGS)

/** Hook for checking if user has analytics access */
export const useCanViewAnalytics = (): boolean => useHasPermission(PERMISSIONS.ANALYTICS)
